import { useEffect, useState } from "react";
import style from "./create-monday-item-sections.module.css";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { MondayBoard, MondayColumn } from "shared/integrations/mapper";
import { useAtomValue } from "jotai";
import { syncServiceAtom, utilsAtom } from "state-atoms";
import { groupBy, prop, zipObj } from "rambda";
import { ReadTransaction } from "@workcanvas/reflect";
import { ExpandableContent, InputFieldsGrid, FieldMapping, PrimaryButton, WarningIcon } from "../components";
import { CreateMondayItemMappingsProps, MondayItemMode } from "../create-monday-item-menu";
import {
  analyzeDataInTaskCards,
  ensureFullIdForTag,
  prepareTaskCardsForMondayIntegration,
  DataInCards,
  TaskLikeElement,
} from "../../logic/utils";
import {
  MappingTaskCardsToMonday,
  useMappingSettingsForTaskCards,
} from "../../logic/gantt-mapping-hooks/use-mapping-settings-task-cards";
import {
  MappingMondayToGanttTasks,
  useMappingSettingsForGantt,
  commitMappingSettingsForGantt,
} from "../../logic/gantt-mapping-hooks/use-mapping-settings-gantt-cards";
import Modal from "frontend/modal/modal";
import * as Sentry from "@sentry/nextjs";
import { ConfirmCreationWithoutMapping } from "../mappings/no-mapping/confirm-if-no-mapping";
import { TypeOfDateMapping } from "../mappings/gantt-mapping/components-gantt-mapping";
import { IntegrationType } from "shared/integrations/integration";
import tracking from "frontend/tracking";

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

// Improved DateTab type that excludes empty string for new logic
type ValidDateTab = "start-end" | "timeline";
type AnyDateTab = ValidDateTab | "";

// Base mapping interface with common fields - extending existing to maintain compatibility
interface ImprovedBaseMondayMapping {
  assignee: string;
  tags: string;
  "date-tab": string;
  start_date: string;
  end_date: string;
  timeline: string;
}

// Enhanced task card mapping with proper date tab typing
interface ImprovedTaskCardMapping extends ImprovedBaseMondayMapping {
  date: string;
  "date-tab": AnyDateTab; // Allow empty, in case of a single date-value
}

// Enhanced gantt card mapping with dependency
interface ImprovedGanttCardMapping extends ImprovedBaseMondayMapping {
  dependency: string;
  "date-tab": ValidDateTab;
}

type AllMappingKeys = keyof (ImprovedTaskCardMapping & ImprovedGanttCardMapping);

// Union type using improved interfaces - maintain compatibility with existing types
type UnifiedMapping = Partial<ImprovedTaskCardMapping> | Partial<ImprovedGanttCardMapping>;

interface CreateItemsParams {
  selectedBoard: MondayBoard;
  nodes: Iterable<any>;
  mapping: UnifiedMapping;
  dateTab: AnyDateTab;
  syncService: any;
  createComplexIntegrationItems: any;
  isSubitem: boolean;
  selectedItemId?: string;
  selectedGroupId: string;
}

interface CreateItemsResult {
  integrationId: string;
  itemIds: Record<string, any>;
  elementIds: string[];
  elements: TaskLikeElement[];
}

type MappingOptions = Record<AllMappingKeys, MondayColumn[]>;

// =============================================================================
// FUNCTIONS FOR BUSINESS LOGIC
// =============================================================================

/**
 * Calculate available mapping options based on board columns
 */
function calculateMappingOptions(columnsByType: Record<string, MondayColumn[]>): Omit<MappingOptions, "date-tab"> {
  return {
    timeline: columnsByType["timeline"],
    date: columnsByType["date"],
    assignee: columnsByType["people"],
    tags: columnsByType["dropdown"],
    start_date: columnsByType["date"],
    end_date: columnsByType["date"],
    dependency: columnsByType["dependency"],
  };
}

/**
 * Calculate date tab options based on available columns
 */
interface OptionsDateTab {
  timeline: boolean;
  "start-end": boolean;
}
function calculateDateTabOptions(options: { timeline: any; date: any }): OptionsDateTab {
  return {
    timeline: !!options.timeline?.length,
    "start-end": !!options.date?.length && options.date.length >= 2,
  };
}

/**
 * Check if we're missing some mapping for the cards we're working on
 */
function isMissingMapping(
  cardsAnalysis: DataInCards,
  dateTab: AnyDateTab,
  mapping: undefined | MappingTaskCardsToMonday | MappingMondayToGanttTasks
) {
  if (!mapping) return true;
  const isMissing =
    (cardsAnalysis.haveTags && !mapping.tags) ||
    (cardsAnalysis.haveAssignees && !mapping.assignee) ||
    (cardsAnalysis.hasSingleDate && !mapping.date) ||
    (cardsAnalysis.hasTimeRange &&
      (!dateTab ||
        (dateTab == "timeline" && !mapping.timeline) ||
        (dateTab == "start-end" && !(mapping.start_date && mapping.end_date))));
  return isMissing;
}

// TODO: same functionality as isMissingMapping.. unite
function checkAbilityToConvert(
  cardsAnalysis: DataInCards,
  mode: MondayItemMode,
  options: ReturnType<typeof calculateMappingOptions>,
  optionDateTab: OptionsDateTab
) {
  const critical: string[] = [];
  const warnings: string[] = [];

  if (cardsAnalysis.haveAssignees && !options.assignee?.length) {
    warnings.push("No person column");
  }
  if (cardsAnalysis.haveTags && !options.tags?.length) {
    warnings.push("No tags column");
  }
  if (mode == "task-cards") {
    if (cardsAnalysis.hasTimeRange && !(optionDateTab.timeline || optionDateTab["start-end"])) {
      warnings.push("No date or timeline columns");
    } else if (cardsAnalysis.hasSingleDate && !options.date?.length) {
      warnings.push("No date column");
    }
  } else {
    if (!optionDateTab.timeline && !optionDateTab["start-end"]) {
      critical.push("Board has no dates or timeline columns");
    }
  }
  return { critical, warnings };
}

/**
 * Core creation logic
 */
async function createItems({
  selectedBoard,
  nodes,
  mapping,
  dateTab,
  syncService,
  createComplexIntegrationItems,
  isSubitem,
  selectedItemId,
  selectedGroupId,
}: CreateItemsParams): Promise<CreateItemsResult | null> {
  const ids: string[] = [];
  const elements: TaskLikeElement[] = [];
  for (const node of nodes) {
    ids.push(node.attrs.id);
    elements.push(node.attrs.element as TaskLikeElement);
  }

  // Get the textual names of tags mentioned in the cards
  let tagNames: undefined | Record<string, any>;
  if (mapping?.tags) {
    tagNames = await syncService
      ?.getReplicache()
      ?.query(async (tx: ReadTransaction) =>
        Object.fromEntries(await tx.scan({ prefix: "cMetadata-Tag-" }).entries().toArray())
      );
  }

  // Format the cards data for the backend
  const formattedItems = prepareTaskCardsForMondayIntegration(
    elements,
    mapping as Partial<MappingTaskCardsToMonday>,
    dateTab,
    (tagId: string) => tagNames?.[ensureFullIdForTag(tagId)]?.name || ""
  );

  const itemsForBackend = zipObj(ids, formattedItems);

  const { integrationId, itemIds } = await createComplexIntegrationItems(
    itemsForBackend,
    selectedBoard,
    isSubitem ? { type: "item", id: selectedItemId! } : { type: "group", id: selectedGroupId! }
  );

  return {
    integrationId,
    itemIds,
    elementIds: ids,
    elements,
  };
}

// =============================================================================
// REACT COMPONENT
// =============================================================================

export function MappedElementsConversion({
  documentId,
  nodes,
  selectedBoard,
  selectedGroupId,
  selectedItemId,
  isMultipleElements,
  isSubitem = false,
  setShowInvalids,
  onCreatedItems,
  mode,
}: CreateMondayItemMappingsProps & { mode: MondayItemMode }) {
  // Hooks and state
  const { mondayIntegration, createComplexIntegrationItems, fetchItems } = useBoardTasks(documentId);
  const [dateTab, setDateTab] = useState<AnyDateTab>("");

  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [alert, setAlert] = useState(false);

  const canvasUtils = useAtomValue(utilsAtom(documentId));
  const syncService = useAtomValue(syncServiceAtom);

  // Board selection
  const actualBoard = isSubitem ? selectedBoard?.subitem_board : selectedBoard;
  const boardId = actualBoard?.id;
  const columns = actualBoard?.columns || [];
  const columnsByType = groupBy(prop("type"), columns);

  // Mode-specific mapping hooks
  //TODO: unite
  const [taskMapping, patchTaskMapping] = useMappingSettingsForTaskCards(syncService, boardId);
  const [ganttMapping, setGanttMapping] = useMappingSettingsForGantt(syncService, boardId);

  const mapping = mode === "task-cards" ? taskMapping : ganttMapping;
  const changeMapping =
    mode === "task-cards" ? patchTaskMapping : (o: any) => setGanttMapping((prev) => ({ ...prev, ...o }));

  // Analyze cards
  const creatingElement = isMultipleElements ? (isSubitem ? "subitems" : "items") : isSubitem ? "subitem" : "item";
  const cardAnalysis = analyzeDataInTaskCards(nodes);

  const options = calculateMappingOptions(columnsByType);
  const optionDateTab = calculateDateTabOptions(options);

  // reset some things when changing board (todo: should be done in the setBoard handler, not in useEffect)
  useEffect(() => {
    setShowInvalids(false);
    setDateTab(optionDateTab.timeline ? "timeline" : optionDateTab["start-end"] ? "start-end" : "");
  }, [actualBoard]);

  // Check if we have options in the board to map all data fields in the cards.
  const { warnings, critical } = checkAbilityToConvert(cardAnalysis, mode, options, optionDateTab);
  const cardHasSomeData =
    cardAnalysis.haveAssignees || cardAnalysis.haveTags || cardAnalysis.hasSingleDate || cardAnalysis.hasTimeRange;

  // TODO: for task-cards, should we allow mapping date-range to a single date columns ??
  // For gantt cards we surely don't allow
  const canCreateItem = !!actualBoard && (isSubitem ? selectedItemId : selectedGroupId) && critical.length === 0;

  // We show the mapping section once board is selected, and assuming that card has some data to transfer.
  const shouldShowAnyMapping: boolean = !!selectedBoard && cardHasSomeData && critical.length === 0;

  // Helper function for field mapping
  const mappingFor = (id: keyof MappingTaskCardsToMonday, label: string, columns?: MondayColumn[]) =>
    columns?.length ? (
      <FieldMapping
        key={id}
        testId={id}
        label={label}
        value={mapping?.[id]}
        onChange={(colId: string) => changeMapping({ [id]: colId })}
        options={columns}
        theme="dark"
      />
    ) : null;

  const handleCreateItemClicked = async () => {
    if (!selectedBoard || !nodes || !mondayIntegration || !syncService || !mapping) return;

    setIsCreatingItem(true);
    try {
      // Commit mapping settings for gantt cards
      if (mode === "gantt-cards" && boardId) {
        await commitMappingSettingsForGantt(syncService, boardId, mapping as MappingMondayToGanttTasks);
      }

      const result = await createItems({
        selectedBoard,
        nodes,
        mapping,
        dateTab,
        syncService,
        createComplexIntegrationItems,
        isSubitem,
        selectedItemId,
        selectedGroupId,
      });
      tracking.trackAnalyticsEvent("convert-to-monday-item-clicked", {
        isSubitem,
        isMultipleElements,
        convertToDataLayer: false,
      });

      if (result && canvasUtils) {
        // Fetch the newly created Monday items to load their data
        const createdItemIds = Object.values(result.itemIds);
        await fetchItems({
          [result.integrationId]: {
            itemIds: createdItemIds,
          },
        });

        const itemsToCreate = result.elementIds.map((id, index) => ({
          itemId: result.itemIds[id], // the created monday item id
          element: result.elements[index], // the original task card
          elementId: id, // the original task card id
        }));
        const { newIds, oldIds } = await canvasUtils.addIntegrationItems(
          itemsToCreate,
          result.integrationId,
          IntegrationType.monday,
          syncService!,
          false
        );
        canvasUtils.replaceCanvasElements(newIds, oldIds);
        onCreatedItems();
      }
    } catch (error: any) {
      if (error?.response?.status === 403) {
        // We should never get here!
        Sentry.captureException(error, { tags: { type: "monday-integration-authorization-required" } });
        return;
      }
      console.error("failed to create item", error);
      // TODO: alert user
    } finally {
      setIsCreatingItem(false);
    }
  };

  return (
    <>
      <ExpandableContent open={shouldShowAnyMapping}>
        <span>Map task fields to columns</span>
        <InputFieldsGrid>
          {cardAnalysis.hasTimeRange && (optionDateTab.timeline || optionDateTab["start-end"]) && (
            <div style={{ gridColumn: "1/-1" }}>
              <TypeOfDateMapping
                name="item-date-tab"
                value={dateTab}
                onChange={(c) => setDateTab(c as AnyDateTab)}
                disableDates={!optionDateTab["start-end"]}
                disableTimeline={!optionDateTab.timeline}
                theme="dark"
              />
            </div>
          )}

          {/* Date fields based on mode and selection */}
          {/* This puts date fields first, for gantt and task cards. check with Adi */}
          {cardAnalysis.hasTimeRange && dateTab === "timeline" && mappingFor("timeline", "Timeline", options.timeline)}
          {cardAnalysis.hasTimeRange && dateTab === "start-end" && (
            <>
              {mappingFor("start_date", "Start", options.start_date)}
              {mappingFor("end_date", "End", options.end_date)}
            </>
          )}
          {cardAnalysis.hasSingleDate && mappingFor("date", "Date", options.date)}

          {/* Common fields */}
          {cardAnalysis.haveAssignees && mappingFor("assignee", "Assignee", options.assignee)}
          {cardAnalysis.haveTags && mappingFor("tags", "Tags", options.tags)}
        </InputFieldsGrid>
        {warnings.length > 0 && !!actualBoard && (
          <div className={style.warningArea}>
            <WarningIcon style={{ margin: "auto", width: "100%" }} />
            <div style={{ fontWeight: 500 }}>This board is missing columns, data will be lost !</div>
            <ul style={{ margin: 0, paddingInline: "1em" }}>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </ExpandableContent>

      <ExpandableContent open={critical.length > 0 && !!actualBoard}>
        <div className={style.warningArea}>
          <WarningIcon style={{ margin: "auto", width: "100%" }} />
          <div style={{ margin: "auto", width: "100%", textAlign: "center" }}>Cannot create monday item</div>
          <ul style={{ margin: 0, paddingInline: "1em" }}>
            {critical.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      </ExpandableContent>

      <PrimaryButton
        disabled={!canCreateItem}
        onClick={(e) => {
          e.preventDefault();
          if (isCreatingItem) {
            return;
          }
          if (!canCreateItem) {
            setShowInvalids(true);
            return;
          }

          if (isMissingMapping(cardAnalysis, dateTab, mapping)) {
            setAlert(true);
          } else {
            handleCreateItemClicked();
          }
        }}
      >
        {isCreatingItem ? (isMultipleElements ? "Creating items..." : "Creating item...") : `Create ${creatingElement}`}
      </PrimaryButton>

      {alert && (
        <Modal dimBackground={true}>
          <ConfirmCreationWithoutMapping
            onContinue={function (): void {
              handleCreateItemClicked();
              setAlert(false);
            }}
            onBackToMapping={() => setAlert(false)}
          />
        </Modal>
      )}
    </>
  );
}
